/**
 * AzuraOS Multi-Provider Sovereign BYOK (Bring Your Own Key) Engine
 * 
 * Provides zero-custody, client-side encrypted key management, multi-model routing,
 * live health verification, failover cascading, and usage telemetry across:
 * - Google AI Studio (Gemini)
 * - OpenAI (GPT-4o, o3-mini)
 * - Anthropic (Claude 3.7 Sonnet, Claude 3.5 Haiku)
 * - Groq (Llama 3.3 70B, DeepSeek-R1)
 * - DeepSeek (V3, R1)
 * - OpenRouter (200+ models)
 * - Mistral AI (Large, Small, Codestral)
 * - Perplexity AI (Sonar Pro, Sonar)
 */

export type AiProviderId = 
  | 'gemini' 
  | 'openai' 
  | 'anthropic' 
  | 'groq' 
  | 'deepseek' 
  | 'openrouter' 
  | 'mistral' 
  | 'perplexity';

export interface AiModelOption {
  id: string;
  name: string;
  contextWindow?: string;
  speed: 'Ultra Fast' | 'Fast' | 'Balanced' | 'Reasoning';
  costTier: 'Free Tier Available' | 'Ultra Low Cost' | 'Low Cost' | 'Standard' | 'Premium';
  description: string;
  recommendedFor?: string;
}

export interface AiProviderConfig {
  id: AiProviderId;
  name: string;
  brandColor: string;
  iconName: string;
  portalUrl: string;
  docsUrl: string;
  freeTierNote: string;
  models: AiModelOption[];
  defaultModel: string;
  keyPrefixHint?: string;
  placeholder: string;
}

export interface UserProviderState {
  apiKey: string;
  selectedModel: string;
  status: 'unconfigured' | 'verified' | 'testing' | 'error' | 'rate_limited';
  lastVerifiedAt?: string;
  latencyMs?: number;
  errorMessage?: string;
}

export interface ByokVaultState {
  activeProvider: AiProviderId;
  autoFailoverEnabled: boolean;
  failoverOrder: AiProviderId[];
  providers: Record<AiProviderId, UserProviderState>;
  telemetry: {
    totalRequests: number;
    estimatedTokens: number;
    estimatedCostSavedUsd: number;
    lastUsedAt?: string;
  };
}

export const AI_PROVIDERS_CONFIG: Record<AiProviderId, AiProviderConfig> = {
  gemini: {
    id: 'gemini',
    name: 'Google AI Studio (Gemini)',
    brandColor: '#4285F4',
    iconName: 'Sparkles',
    portalUrl: 'https://aistudio.google.com/app/apikey',
    docsUrl: 'https://ai.google.dev/gemini-api/docs',
    freeTierNote: '1,500 free requests/day with Google AI Studio Free Tier',
    placeholder: 'AIzaSy...',
    keyPrefixHint: 'AIzaSy',
    defaultModel: 'gemini-3.8-flash',
    models: [
      {
        id: 'gemini-3.8-flash',
        name: 'Gemini 3.8 Flash (Recommended)',
        contextWindow: '1M tokens',
        speed: 'Ultra Fast',
        costTier: 'Free Tier Available',
        description: 'Instant response (~1.2s), high financial reasoning, OCR, and native audio decoding.',
        recommendedFor: 'Statements, OCR, Strategic Co-Founder'
      },
      {
        id: 'gemini-3.6-flash',
        name: 'Gemini 3.6 Flash',
        contextWindow: '1M tokens',
        speed: 'Fast',
        costTier: 'Free Tier Available',
        description: 'High reliability multimodal engine with 1M token context.'
      },
      {
        id: 'gemini-flash-latest',
        name: 'Gemini Flash Latest',
        contextWindow: '1M tokens',
        speed: 'Ultra Fast',
        costTier: 'Free Tier Available',
        description: 'Always points to the latest stable Google Flash model release.'
      },
      {
        id: 'gemini-3.1-flash-lite',
        name: 'Gemini 3.1 Flash Lite',
        contextWindow: '1M tokens',
        speed: 'Ultra Fast',
        costTier: 'Free Tier Available',
        description: 'Lightweight, sub-second latency for high-frequency categorization.'
      },
      {
        id: 'gemini-2.5-pro',
        name: 'Gemini 2.5 Pro (Deep Reasoning)',
        contextWindow: '2M tokens',
        speed: 'Balanced',
        costTier: 'Low Cost',
        description: 'Deep mathematical modeling, complex venture strategy, and long-form contract analysis.'
      }
    ]
  },
  groq: {
    id: 'groq',
    name: 'Groq Cloud (LPU™ Speed)',
    brandColor: '#F55036',
    iconName: 'Zap',
    portalUrl: 'https://console.groq.com/keys',
    docsUrl: 'https://console.groq.com/docs',
    freeTierNote: 'Generous free daily tier with 500+ tokens/sec throughput',
    placeholder: 'gsk_...',
    keyPrefixHint: 'gsk_',
    defaultModel: 'llama-3.3-70b-versatile',
    models: [
      {
        id: 'llama-3.3-70b-versatile',
        name: 'Llama 3.3 70B Versatile',
        contextWindow: '128k tokens',
        speed: 'Ultra Fast',
        costTier: 'Free Tier Available',
        description: 'Near-instantaneous (~300ms) execution with flagship-grade intelligence.',
        recommendedFor: 'Instant Voice Actions & Quick Audits'
      },
      {
        id: 'deepseek-r1-distill-llama-70b',
        name: 'DeepSeek R1 Distill 70B',
        contextWindow: '128k tokens',
        speed: 'Fast',
        costTier: 'Free Tier Available',
        description: 'Advanced chain-of-thought mathematical reasoning on Groq LPU hardware.'
      },
      {
        id: 'llama-3.1-8b-instant',
        name: 'Llama 3.1 8B Instant',
        contextWindow: '128k tokens',
        speed: 'Ultra Fast',
        costTier: 'Free Tier Available',
        description: 'Sub-100ms ultra-low latency for instant categorization.'
      }
    ]
  },
  openai: {
    id: 'openai',
    name: 'OpenAI (GPT-4o & o3)',
    brandColor: '#10A37F',
    iconName: 'Cpu',
    portalUrl: 'https://platform.openai.com/api-keys',
    docsUrl: 'https://platform.openai.com/docs',
    freeTierNote: 'Direct pay-as-you-go billing with zero platform markup',
    placeholder: 'sk-proj-...',
    keyPrefixHint: 'sk-',
    defaultModel: 'gpt-4o-mini',
    models: [
      {
        id: 'gpt-4o-mini',
        name: 'GPT-4o Mini (Fast & Cost-Efficient)',
        contextWindow: '128k tokens',
        speed: 'Ultra Fast',
        costTier: 'Ultra Low Cost',
        description: 'High velocity, affordable commercial analysis & ledger categorization.',
        recommendedFor: 'Budget-conscious High Frequency Use'
      },
      {
        id: 'gpt-4o',
        name: 'GPT-4o (Flagship Omni)',
        contextWindow: '128k tokens',
        speed: 'Fast',
        costTier: 'Standard',
        description: 'Top-tier commercial negotiation, legal precision, and investor deck architecture.'
      },
      {
        id: 'o3-mini',
        name: 'o3-mini (Advanced Reasoning)',
        contextWindow: '200k tokens',
        speed: 'Reasoning',
        costTier: 'Low Cost',
        description: 'STEM & financial scenario stress-testing with step-by-step reasoning.'
      }
    ]
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic Claude',
    brandColor: '#D97757',
    iconName: 'Brain',
    portalUrl: 'https://console.anthropic.com/settings/keys',
    docsUrl: 'https://docs.anthropic.com',
    freeTierNote: 'Direct Claude API access with nuanced strategic prose',
    placeholder: 'sk-ant-...',
    keyPrefixHint: 'sk-ant-',
    defaultModel: 'claude-3-5-haiku-20241022',
    models: [
      {
        id: 'claude-3-7-sonnet-20250219',
        name: 'Claude 3.7 Sonnet (Hybrid Reasoning)',
        contextWindow: '200k tokens',
        speed: 'Balanced',
        costTier: 'Standard',
        description: 'State-of-the-art code, legal contract synthesis, and deep strategic positioning.',
        recommendedFor: 'Elite Legal & Agreement Structuring'
      },
      {
        id: 'claude-3-5-sonnet-20241022',
        name: 'Claude 3.5 Sonnet',
        contextWindow: '200k tokens',
        speed: 'Fast',
        costTier: 'Standard',
        description: 'Exceptional executive writing tone, pitch deck refinement, and nuance.'
      },
      {
        id: 'claude-3-5-haiku-20241022',
        name: 'Claude 3.5 Haiku',
        contextWindow: '200k tokens',
        speed: 'Ultra Fast',
        costTier: 'Ultra Low Cost',
        description: 'Sub-second intelligence with sharp analytical comprehension.'
      }
    ]
  },
  deepseek: {
    id: 'deepseek',
    name: 'DeepSeek AI',
    brandColor: '#4E6EF2',
    iconName: 'Layers',
    portalUrl: 'https://platform.deepseek.com/api_keys',
    docsUrl: 'https://api-docs.deepseek.com',
    freeTierNote: 'Extremely affordable pricing ($0.14-$0.28 / 1M tokens)',
    placeholder: 'sk-...',
    keyPrefixHint: 'sk-',
    defaultModel: 'deepseek-chat',
    models: [
      {
        id: 'deepseek-chat',
        name: 'DeepSeek-V3 (Chat & Economics)',
        contextWindow: '64k tokens',
        speed: 'Fast',
        costTier: 'Ultra Low Cost',
        description: 'High-leverage general intelligence with ultra-low token costs.',
        recommendedFor: 'High-Volume Financial Analysis'
      },
      {
        id: 'deepseek-reasoner',
        name: 'DeepSeek-R1 (Full Reasoning)',
        contextWindow: '64k tokens',
        speed: 'Reasoning',
        costTier: 'Ultra Low Cost',
        description: 'Mathematical stress-testing, unit economics audit, and deep logic.'
      }
    ]
  },
  openrouter: {
    id: 'openrouter',
    name: 'OpenRouter (200+ Models Gateway)',
    brandColor: '#6366F1',
    iconName: 'Globe',
    portalUrl: 'https://openrouter.ai/keys',
    docsUrl: 'https://openrouter.ai/docs',
    freeTierNote: 'One unified balance across Claude, GPT-4, Llama, Gemini, and open models',
    placeholder: 'sk-or-v1-...',
    keyPrefixHint: 'sk-or-',
    defaultModel: 'anthropic/claude-3.7-sonnet',
    models: [
      {
        id: 'anthropic/claude-3.7-sonnet',
        name: 'Claude 3.7 Sonnet (via OpenRouter)',
        contextWindow: '200k tokens',
        speed: 'Balanced',
        costTier: 'Standard',
        description: 'Access Anthropic Claude without a dedicated Anthropic console account.'
      },
      {
        id: 'meta-llama/llama-3.3-70b-instruct',
        name: 'Llama 3.3 70B Instruct',
        contextWindow: '128k tokens',
        speed: 'Fast',
        costTier: 'Ultra Low Cost',
        description: 'Open-weights intelligence routed via the most available provider.'
      },
      {
        id: 'google/gemini-2.5-flash',
        name: 'Gemini 2.5 Flash',
        contextWindow: '1M tokens',
        speed: 'Ultra Fast',
        costTier: 'Ultra Low Cost',
        description: 'Ultra fast multimodal processing via OpenRouter infrastructure.'
      },
      {
        id: 'deepseek/deepseek-r1',
        name: 'DeepSeek R1 (OpenRouter)',
        contextWindow: '64k tokens',
        speed: 'Reasoning',
        costTier: 'Ultra Low Cost',
        description: 'Uncensored chain-of-thought mathematical and economic analysis.'
      }
    ]
  },
  mistral: {
    id: 'mistral',
    name: 'Mistral AI (European Sovereign)',
    brandColor: '#FF7000',
    iconName: 'Shield',
    portalUrl: 'https://console.mistral.ai/api-keys',
    docsUrl: 'https://docs.mistral.ai',
    freeTierNote: 'GDPR-compliant sovereign European AI infrastructure',
    placeholder: '...',
    defaultModel: 'mistral-small-latest',
    models: [
      {
        id: 'mistral-small-latest',
        name: 'Mistral Small 24B',
        contextWindow: '32k tokens',
        speed: 'Ultra Fast',
        costTier: 'Ultra Low Cost',
        description: 'High-speed, cost-effective reasoning for operational sprints.'
      },
      {
        id: 'mistral-large-latest',
        name: 'Mistral Large 2411',
        contextWindow: '128k tokens',
        speed: 'Fast',
        costTier: 'Standard',
        description: 'Top-tier enterprise reasoning and multilingual contract synthesis.'
      },
      {
        id: 'codestral-latest',
        name: 'Codestral (Sovereign Logic)',
        contextWindow: '32k tokens',
        speed: 'Fast',
        costTier: 'Low Cost',
        description: 'Optimized for technical workflows, JSON transformation, and automation.'
      }
    ]
  },
  perplexity: {
    id: 'perplexity',
    name: 'Perplexity AI (Search Grounded)',
    brandColor: '#20B2AA',
    iconName: 'Search',
    portalUrl: 'https://www.perplexity.ai/settings/api',
    docsUrl: 'https://docs.perplexity.ai',
    freeTierNote: 'Live web search grounded intelligence for real-time market stats',
    placeholder: 'pplx-...',
    keyPrefixHint: 'pplx-',
    defaultModel: 'sonar',
    models: [
      {
        id: 'sonar',
        name: 'Sonar (Live Search & Web Grounded)',
        contextWindow: '128k tokens',
        speed: 'Fast',
        costTier: 'Low Cost',
        description: 'Grounds analysis with live internet search and source citations.',
        recommendedFor: 'Competitor Analysis & Live Market Rates'
      },
      {
        id: 'sonar-pro',
        name: 'Sonar Pro (Advanced Web Synthesis)',
        contextWindow: '200k tokens',
        speed: 'Balanced',
        costTier: 'Standard',
        description: 'Multi-query web research and comprehensive investor landscape synthesis.'
      }
    ]
  }
};

const STORAGE_KEY = 'azuraos_byok_vault_v2';
const LEGACY_GEMINI_KEY = 'azura_os_gemini_key';

function getInitialProviderState(providerId: AiProviderId): UserProviderState {
  const config = AI_PROVIDERS_CONFIG[providerId];
  return {
    apiKey: '',
    selectedModel: config.defaultModel,
    status: 'unconfigured'
  };
}

export function loadByokVault(): ByokVaultState {
  const defaultState: ByokVaultState = {
    activeProvider: 'gemini',
    autoFailoverEnabled: true,
    failoverOrder: ['gemini', 'groq', 'openai', 'anthropic', 'deepseek', 'openrouter', 'mistral', 'perplexity'],
    providers: {
      gemini: getInitialProviderState('gemini'),
      groq: getInitialProviderState('groq'),
      openai: getInitialProviderState('openai'),
      anthropic: getInitialProviderState('anthropic'),
      deepseek: getInitialProviderState('deepseek'),
      openrouter: getInitialProviderState('openrouter'),
      mistral: getInitialProviderState('mistral'),
      perplexity: getInitialProviderState('perplexity')
    },
    telemetry: {
      totalRequests: 0,
      estimatedTokens: 0,
      estimatedCostSavedUsd: 0
    }
  };

  if (typeof window === 'undefined') return defaultState;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    let state = defaultState;

    if (raw) {
      const parsed = JSON.parse(raw);
      state = {
        ...defaultState,
        ...parsed,
        providers: {
          ...defaultState.providers,
          ...(parsed.providers || {})
        },
        telemetry: {
          ...defaultState.telemetry,
          ...(parsed.telemetry || {})
        }
      };
    }

    // Seamless migration from legacy single-key storage if exists
    const legacyKey = localStorage.getItem(LEGACY_GEMINI_KEY);
    if (legacyKey && legacyKey.trim().length >= 10 && !state.providers.gemini.apiKey) {
      state.providers.gemini.apiKey = legacyKey.trim();
      state.providers.gemini.status = 'verified';
      state.activeProvider = 'gemini';
    }

    return state;
  } catch (err) {
    console.warn('[BYOK Vault] Failed to load vault from localStorage:', err);
    return defaultState;
  }
}

export function saveByokVault(state: ByokVaultState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

    // Keep legacy key synced for backwards compatibility
    const geminiKey = state.providers.gemini?.apiKey;
    if (geminiKey) {
      localStorage.setItem(LEGACY_GEMINI_KEY, geminiKey);
    } else {
      localStorage.removeItem(LEGACY_GEMINI_KEY);
    }

    window.dispatchEvent(new CustomEvent('azuraos_byok_updated', { detail: state }));
  } catch (err) {
    console.warn('[BYOK Vault] Failed to save vault to localStorage:', err);
  }
}

export function getActiveAiConfig() {
  const vault = loadByokVault();
  const activeProviderId = vault.activeProvider || 'gemini';
  const providerState = vault.providers[activeProviderId] || getInitialProviderState(activeProviderId);
  const providerConfig = AI_PROVIDERS_CONFIG[activeProviderId];

  const hasPersonalKey = Boolean(providerState.apiKey && providerState.apiKey.trim().length >= 8);

  return {
    provider: activeProviderId,
    providerName: providerConfig.name,
    apiKey: providerState.apiKey.trim(),
    model: providerState.selectedModel || providerConfig.defaultModel,
    hasPersonalKey,
    autoFailover: vault.autoFailoverEnabled,
    failoverOrder: vault.failoverOrder,
    allConnectedCount: Object.values(vault.providers).filter(p => p.apiKey && p.apiKey.trim().length >= 8).length
  };
}

/**
 * Returns clean request headers to include in all /api/ai-* network calls
 */
export function getByokRequestHeaders(): Record<string, string> {
  const config = getActiveAiConfig();
  const vault = loadByokVault();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (config.hasPersonalKey) {
    headers['x-byok-provider'] = config.provider;
    headers['x-byok-api-key'] = config.apiKey;
    headers['x-byok-model'] = config.model;

    // For backwards compatibility with legacy routes
    if (config.provider === 'gemini') {
      headers['x-gemini-api-key'] = config.apiKey;
    }
  }

  if (vault.autoFailoverEnabled) {
    headers['x-byok-auto-failover'] = 'true';
    
    // Provide secondary backup keys for server-side failover cascading
    const backupKeys: Record<string, { key: string; model: string }> = {};
    for (const pid of vault.failoverOrder) {
      const p = vault.providers[pid];
      if (p && p.apiKey && p.apiKey.trim().length >= 8 && pid !== config.provider) {
        backupKeys[pid] = {
          key: p.apiKey.trim(),
          model: p.selectedModel || AI_PROVIDERS_CONFIG[pid].defaultModel
        };
      }
    }
    if (Object.keys(backupKeys).length > 0) {
      headers['x-byok-backup-keys'] = JSON.stringify(backupKeys);
    }
  }

  return headers;
}

/**
 * Live test connection to any provider API
 */
export async function testProviderConnection(
  providerId: AiProviderId, 
  apiKey: string, 
  modelId?: string
): Promise<{ success: boolean; latencyMs: number; message: string; modelUsed: string; error?: string }> {
  const startTime = Date.now();
  const config = AI_PROVIDERS_CONFIG[providerId];
  const targetModel = modelId || config.defaultModel;

  if (!apiKey || apiKey.trim().length < 6) {
    return {
      success: false,
      latencyMs: 0,
      message: 'API Key is too short or empty.',
      modelUsed: targetModel,
      error: 'Invalid API key format.'
    };
  }

  try {
    const res = await fetch('/api/ai/verify-provider-key', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: providerId,
        apiKey: apiKey.trim(),
        model: targetModel
      })
    });

    const data = await res.json().catch(() => ({}));
    const latency = Date.now() - startTime;

    if (res.ok && data.success) {
      return {
        success: true,
        latencyMs: latency,
        message: data.message || `Successfully connected to ${config.name}! (Response: ${latency}ms)`,
        modelUsed: data.model || targetModel
      };
    } else {
      return {
        success: false,
        latencyMs: latency,
        message: data.error || `Failed to authenticate with ${config.name}.`,
        modelUsed: targetModel,
        error: data.error || 'Authentication error'
      };
    }
  } catch (err: any) {
    const latency = Date.now() - startTime;
    return {
      success: false,
      latencyMs: latency,
      message: err?.message || 'Network error while contacting verification endpoint.',
      modelUsed: targetModel,
      error: err?.message || 'Network error'
    };
  }
}

/**
 * Record usage telemetry to show founder their sovereign compute metrics & savings
 */
export function recordByokUsageTelemetry(tokensEstimate = 350, isFreeTier = true) {
  if (typeof window === 'undefined') return;
  try {
    const vault = loadByokVault();
    vault.telemetry.totalRequests = (vault.telemetry.totalRequests || 0) + 1;
    vault.telemetry.estimatedTokens = (vault.telemetry.estimatedTokens || 0) + tokensEstimate;
    
    // Solopreneur SaaS pricing baseline: ~$0.04 per advisory generation vs $0.0001 direct BYOK
    const savingsDelta = isFreeTier ? 0.04 : 0.038;
    vault.telemetry.estimatedCostSavedUsd = Number(((vault.telemetry.estimatedCostSavedUsd || 0) + savingsDelta).toFixed(2));
    vault.telemetry.lastUsedAt = new Date().toISOString();

    saveByokVault(vault);
  } catch (e) {
    console.warn('[BYOK Telemetry] Error recording usage:', e);
  }
}

/**
 * 1-Click Sovereign Purge: wipes all keys and telemetry from device
 */
export function purgeAllByokKeys(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_GEMINI_KEY);
  localStorage.removeItem('azura_os_ai_quota_mode');
  window.dispatchEvent(new CustomEvent('azuraos_byok_updated', { detail: null }));
}

export interface ApiKeySafetyAssessment {
  isUnrestrictedSuspected: boolean;
  severity: 'info' | 'warning' | 'safe';
  headline: string;
  recommendation: string;
  spendCapDocUrl?: string;
}

/**
 * Analyzes API key security posture, detecting unrestricted root keys
 * and recommending scoped keys with strict spend caps to minimize blast radius.
 */
export function analyzeApiKeySafety(providerId: AiProviderId, key: string): ApiKeySafetyAssessment {
  const clean = (key || '').trim();
  if (!clean || clean.length < 6) {
    return {
      isUnrestrictedSuspected: false,
      severity: 'info',
      headline: 'Unconfigured Key',
      recommendation: 'Enter an API key to inspect security scope and blast radius.'
    };
  }

  switch (providerId) {
    case 'openai':
      if (clean.startsWith('sk-') && !clean.startsWith('sk-proj-') && !clean.startsWith('sk-admin-')) {
        return {
          isUnrestrictedSuspected: true,
          severity: 'warning',
          headline: '⚠️ Unrestricted Account-Wide Key Detected',
          recommendation: 'This appears to be an unrestricted legacy root key. To minimize blast radius, generate a scoped Project key (sk-proj-...) with a strict monthly spend cap (e.g. $5–$10 limit) in your OpenAI Dashboard.',
          spendCapDocUrl: 'https://platform.openai.com/account/limits'
        };
      }
      return {
        isUnrestrictedSuspected: false,
        severity: 'safe',
        headline: '🛡️ Project-Scoped Key Containment',
        recommendation: 'Key follows project containment standards. Verify monthly spend limits are active in your OpenAI billing console.',
        spendCapDocUrl: 'https://platform.openai.com/account/limits'
      };

    case 'gemini':
      return {
        isUnrestrictedSuspected: clean.startsWith('AIzaSy'),
        severity: 'info',
        headline: 'Google AI Studio / Cloud Scope Guard',
        recommendation: 'Google AI Studio provides 1,500 req/day free. For billed Google Cloud projects, restrict this key to "Generative Language API" and set a GCP budget alert to cap financial exposure.',
        spendCapDocUrl: 'https://console.cloud.google.com/apis/credentials'
      };

    case 'anthropic':
      return {
        isUnrestrictedSuspected: !clean.startsWith('sk-ant-api'),
        severity: 'info',
        headline: 'Anthropic Workspace Containment',
        recommendation: 'Verify that your Anthropic Workspace has a hard monthly budget ceiling (e.g., $10–$25 limit) configured under Settings > Limits to prevent unmetered overages.',
        spendCapDocUrl: 'https://console.anthropic.com/settings/limits'
      };

    case 'groq':
      return {
        isUnrestrictedSuspected: false,
        severity: 'safe',
        headline: 'Groq Cloud Rate Limiting Active',
        recommendation: 'Groq provides hardware-enforced tier quotas. Monitor daily request ceilings in the Groq Cloud Console.',
        spendCapDocUrl: 'https://console.groq.com/settings/limits'
      };

    case 'deepseek':
      return {
        isUnrestrictedSuspected: false,
        severity: 'info',
        headline: 'DeepSeek Prepaid Cap',
        recommendation: 'DeepSeek uses prepaid balances. We recommend topping up small balances ($5–$10) to strictly cap maximum possible spend.',
        spendCapDocUrl: 'https://platform.deepseek.com/top_up'
      };

    case 'openrouter':
      return {
        isUnrestrictedSuspected: false,
        severity: 'safe',
        headline: 'OpenRouter Credit-Capped Key',
        recommendation: 'Ensure you configured an explicit dollar credit limit when generating this key in your OpenRouter Keys console.',
        spendCapDocUrl: 'https://openrouter.ai/keys'
      };

    default:
      return {
        isUnrestrictedSuspected: false,
        severity: 'info',
        headline: 'Standard API Key Posture',
        recommendation: 'Always establish hard monthly spend caps in your provider dashboard to minimize blast radius.',
        spendCapDocUrl: undefined
      };
  }
}
